import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import useAuth from "../../../hooks/useAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Import } from "lucide-react";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { Skeleton } from "../../../components/ui/skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WSContext } from "../../../App";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { config } from "../../../Environment";
import { TooltipProvider } from "../../../components/ui/tooltip";
import LoggerContext from "../../../context/LoggerProvider";
import SingleActivity from "./singleActivity";
import AiAuthoringWizardDialog from "./aiAuthoringWizard";

const MyActivities = ({
  prescriptions,
  setPrescriptions,
  viewPrescription,
  renamePrescription,
  deletePrescription,
  copyPrescriptionAsExample,
  sharePrescription,
  managePermissions,
  exportPrescription,
  runPrescription,
  runPrescriptionById,
  refreshLists,
  isMobile,
}: any) => {
  const { auth } = useAuth();

  const [unfinishedProcessModal, setUnfinishedProcessModal] = useState<{
    isOpen: boolean;
    prescription: any;
  }>({ isOpen: false, prescription: null });
  const [aiAuthoringWizardOpen, setAIAuthoringWizardOpen] = useState(false);

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  const { setListRefresh } = useContext(WSContext);
  const { Logger } = useContext(LoggerContext);

  const newPrescription = () => {
    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("draftprescription");
    navigate("/design");
  };

  const editPrescription = (idx: any) => {
    const prescriptionId = prescriptions[idx]._id;
    sessionStorage.setItem("prescriptionID", prescriptionId);
    sessionStorage.removeItem("draftprescription");
    navigate(`/design?wid=${prescriptionId}`);
  };

  const handleImportPrescription = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async function (e: any) {
        const content = e.target.result;
        try {
          const importData = JSON.parse(content);

          let newResponse = await retryFetch(
            config.API_BASE + "/api/prescriptions/import",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
              body: JSON.stringify(importData),
            }
          );

          let data = await newResponse.json();

          if (!data.name) throw new Error("");
          let newName = data.name;

          toast.success("Prescription was successfully imported as " + newName);

          setListRefresh(true);
        } catch (error) {
          toast.error("Error importing prescription", {
            className: "!bg-red-500 !text-white",
          });

          console.error("Error parsing JSON:", error);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleContinueProcess = () => {
    const latestProcess = unfinishedProcessModal.prescription?.processes?.[0];
    if (latestProcess) {
      console.log(`Navigate to /process/${latestProcess.id}`);
    }
    setUnfinishedProcessModal({ isOpen: false, prescription: null });
  };

  function renderActivitiesList() {
    if (Array.isArray(prescriptions) && prescriptions.length > 0) {
    return (<>
      {prescriptions.map((prescription: any, index: any) => (
                <SingleActivity
                  key={prescription._id}
                  index={index}
                  prescription={prescription}
                  prescriptions={prescriptions}
                  setPrescriptions={setPrescriptions}
                  editPrescription={editPrescription}
                  viewPrescription={viewPrescription}
                  renamePrescription={renamePrescription}
                  deletePrescription={deletePrescription}
                  copyPrescriptionAsExample={copyPrescriptionAsExample}
                  sharePrescription={sharePrescription}
                  managePermissions={managePermissions}
                  exportPrescription={exportPrescription}
                  runPrescription={runPrescription}
                  refreshLists={refreshLists}
                  isMobile={isMobile}
                />
              ))}
    </>);

    }
    return (<></>);
  }

  function renderActivitiesListSkeleton() {
    if (!Array.isArray(prescriptions)) {
    return (<>
      {Array(Math.floor(Math.random() * 10 + 5))
                .join(".")
                .split(".")
                .map((_, arrIdx) => {
                  return (
                    <TableRow key={arrIdx}>
                      <TableCell>
                        <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                      </TableCell>
                    </TableRow>
                  );
                })}
        </>);
    }
    return (<></>);
  }

  function renderActivitiesListEmpty() {
    if (Array.isArray(prescriptions) && prescriptions.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3}>
            {t("no_activities_yet")}
          </TableCell>
        </TableRow>
      );
    }
    return (<></>);
  }

  return (
    <TooltipProvider>
      <div className="px-3 sm:px-4 md:px-0">
      {aiAuthoringWizardOpen && (
          <AiAuthoringWizardDialog
              open={aiAuthoringWizardOpen}
              onClose={() => setAIAuthoringWizardOpen(false)}
              runPrescriptionById={runPrescriptionById}
              refreshActivities={refreshLists}
              isMobile={isMobile}
          />
      )}
        <ul className="flex flex-row gap-6 my-3">
          <li className="hidden md:block">
            <Button
              onClick={() =>
                      newPrescription()
                  }
              disabled={!auth.email}
              className={`${
                auth.email ? "" : "disabledbutton"
              } text-sm md:text-base py-0 md:py-2 h-8 md:h-auto`}
            >
              <FontAwesomeIcon icon={solid("plus")} />
              &nbsp;&nbsp; {t("new_activity")}
            </Button>
          </li>
          <li>
            <Button
              onClick={() =>
                  setAIAuthoringWizardOpen(true)
              }
              disabled={!auth.email}
              className={!auth.email ? "disabledbutton" : ""}
              variant="outline"
            >
              <FontAwesomeIcon icon={solid("wand-magic-sparkles")} className="mr-2 ml-2" />
              {t("ai_assistance")}
            </Button>
          </li>
          <li className="hidden md:block">
            <Button
              onClick={() =>
                (
                  document.querySelector("#importPrescription") as HTMLElement
                )?.click()
              }
              disabled={!auth.email}
              className={!auth.email ? "disabledbutton" : ""}
              variant="outline"
            >
              <Import className="me-2 h-5 w-5" />
              {t("import_activity")}
            </Button>
          </li>
          <li style={{ display: "none" }}>
            <input
              accept=".json"
              type="file"
              id="importPrescription"
              onChange={handleImportPrescription}
            />
          </li>
        </ul>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("name")}</TableHead>
            <TableHead>&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {renderActivitiesListSkeleton()}
          {renderActivitiesListEmpty()}
          {renderActivitiesList()}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};

export default MyActivities;
